import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field, FieldLabel } from '@/components/ui/field';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { usePermissions } from '@/features/permissions';
import { activeEvents } from './projection';
import { useEventActions } from './api';
import { replayIdentity } from './EventHistory';
import type { ClinicalEvent, EventActionInput } from './types';
import type { ProposedEntry } from './PrenatalEntry';
const fields: Record<string, string> = {
  preferred_name: 'Preferred name',
  support_person: 'Partner / support person',
  planned_birth_site: 'Planned birth site',
  backup_hospital: 'Backup hospital',
  backup_phone: 'Backup hospital L&D phone',
  pediatric_provider: 'Pediatric provider',
  assigned_midwife: 'Assigned midwife',
  current_medications: 'Current medications / supplements (as reported)',
  risk_flags: 'Clinical risk flags',
  next_visit_date: 'Next planned visit date',
  clinical_source: 'Source and date for reported clinical status',
};
export function PrenatalContextEditor({
  episodeId,
  subjectId,
  events,
  onSave,
  disabled,
}: {
  episodeId: string;
  subjectId: string;
  events: ClinicalEvent[];
  onSave: (entries: ProposedEntry[], time: string) => Promise<void>;
  disabled: boolean;
}) {
  const { permissions } = usePermissions();
  const action = useEventActions(episodeId);
  const request = useRef<{ rootId: string; input: EventActionInput } | null>(null);
  const [attempted, setAttempted] = useState(false);
  const latest = activeEvents(events)
    .filter((e) => e.event_type === 'PRENATAL.CONTEXT' && e.subject_id === subjectId)
    .at(-1);
  const [editing, setEditing] = useState(false),
    [draft, setDraft] = useState<Record<string, unknown>>({}),
    [head, setHead] = useState<ClinicalEvent | undefined>(),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [reason, setReason] = useState('');
  if (!permissions.includes('create_clinical_event')) return null;
  async function save(event: React.FormEvent) {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(draft).filter(([, v]) => v !== '' && v !== undefined));
      if (!Object.keys(payload).length) throw new Error('Enter at least one documented field.');
      if (
        ['rh_status', 'gbs_status', 'rhig_documented'].some((k) => k in payload) &&
        !String(payload.clinical_source ?? '').trim()
      )
        throw new Error('Record the clinical source and date for laboratory/RhIG status.');
      if (head) {
        if (!reason.trim()) throw new Error('Enter a correction reason.');
        if (!request.current)
          request.current = {
            rootId: head.root_id,
            input: { ...replayIdentity(), kind: 'amend', expected_head_id: head.id, payload, reason },
          };
        setAttempted(true);
        await action.mutateAsync(request.current);
      } else
        await onSave([{ subject_id: subjectId, event_type: 'PRENATAL.CONTEXT', payload }], new Date().toISOString());
      setEditing(false);
      request.current = null;
      setAttempted(false);
    } catch (failure) {
      setError(
        failure instanceof Error && failure.message === 'Enter at least one documented field.'
          ? failure.message
          : 'Could not save context. Your entries are retained; check required source, permissions, and whether the record changed.',
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="flex flex-col gap-3">
      <Button
        type="button"
        variant="outline"
        disabled={busy || editing || disabled || (!!latest && !permissions.includes('amend_clinical_event'))}
        onClick={() => {
          if (request.current) {
            setEditing(true);
            return;
          }
          setDraft({ ...latest?.payload });
          setHead(latest);
          setReason('');
          setError('');
          setEditing(true);
        }}>
        Edit prenatal context
      </Button>
      {editing && (
        <form onSubmit={(event) => void save(event)} className="flex flex-col gap-4 rounded border p-4">
          <p>
            Record documented information only. Unset fields remain unknown. Changes retain prior versions in audit
            history.
          </p>
          <fieldset disabled={busy || attempted} className="grid gap-4 sm:grid-cols-2">
            {Object.entries(fields).map(([key, label]) => (
              <Field key={key}>
                <FieldLabel htmlFor={`context-${key}`}>{label}</FieldLabel>
                {['current_medications', 'risk_flags', 'clinical_source'].includes(key) ? (
                  <Textarea
                    id={`context-${key}`}
                    value={String(draft[key] ?? '')}
                    onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                  />
                ) : (
                  <Input
                    id={`context-${key}`}
                    type={key === 'next_visit_date' ? 'date' : 'text'}
                    value={String(draft[key] ?? '')}
                    onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                  />
                )}
              </Field>
            ))}
            {Object.entries({
              rh_status: ['positive', 'negative', 'unknown'],
              gbs_status: ['positive', 'negative', 'unknown', 'declined'],
              rhig_documented: ['true', 'false'],
              lmp_certain: ['true', 'false'],
            }).map(([key, values]) => (
              <Field key={key}>
                <FieldLabel>
                  {
                    (
                      {
                        rh_status: 'Documented Rh status',
                        gbs_status: 'Documented GBS status',
                        rhig_documented: 'RhIG administration documented',
                        lmp_certain: 'LMP reported certain',
                      } as Record<string, string>
                    )[key]
                  }
                </FieldLabel>
                <Select
                  value={draft[key] === undefined ? 'unset' : String(draft[key])}
                  onValueChange={(value) =>
                    setDraft({
                      ...draft,
                      [key]:
                        value === 'unset'
                          ? undefined
                          : ['rhig_documented', 'lmp_certain'].includes(key)
                            ? value === 'true'
                            : value,
                    })
                  }>
                  <SelectTrigger aria-label={key}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="unset">Not documented</SelectItem>
                      {values.map((value) => (
                        <SelectItem key={value} value={value}>
                          {value === 'true' ? 'Yes' : value === 'false' ? 'No' : value}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </Field>
            ))}
            {head && (
              <Field>
                <FieldLabel htmlFor="context-reason">Reason for correction</FieldLabel>
                <Textarea id="context-reason" required value={reason} onChange={(e) => setReason(e.target.value)} />
              </Field>
            )}
          </fieldset>
          <div className="flex gap-3">
            <Button disabled={busy || disabled} type="submit">
              {attempted ? 'Retry saved context correction' : 'Save documented context'}
            </Button>
            <Button type="button" variant="outline" disabled={busy || attempted} onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
          {error && (
            <p role="alert">
              {error}
              {attempted && ' The exact attempted correction is retained; retry it before starting another correction.'}
            </p>
          )}
        </form>
      )}
    </section>
  );
}
